import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import {
  EmptyState,
  ErrorNotice,
  Heading,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { listTargetedAnalyses } from '@/lib/targeted-analytics';

/** ISO timestamp to a short, readable date; a stable fallback when absent. */
function lastAnalyzed(generatedAt: string | null): string {
  if (!generatedAt) return 'Not built yet';
  const when = new Date(generatedAt);
  if (Number.isNaN(when.getTime())) return 'Not built yet';
  return `Last analyzed ${when.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

/**
 * The targeted-analytics landing page: a card per focused analysis. Each card
 * links to its dedicated page. The list comes from the API, so a new analysis
 * appears here as soon as it is registered and built.
 */
export function TargetedAnalyticsPage(): React.ReactElement {
  const query = useQuery({
    queryKey: ['targeted-analytics', 'list'],
    queryFn: ({ signal }) => listTargetedAnalyses(signal),
  });

  return (
    <div className="space-y-6">
      <PageHeading
        eyebrow="Analytics"
        title="Targeted analytics"
        subtitle="Focused analytics that sit beside the overall dashboards, each with an AI read of what it means."
      />

      {query.isLoading ? (
        <LoadingState label="Loading analytics" />
      ) : query.isError ? (
        <ErrorNotice
          title="Could not load targeted analytics"
          message={errorMessage(query.error)}
        />
      ) : query.data && query.data.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {query.data.map((card) => (
            <Link
              key={card.slug}
              to={`/analytics/targeted/${card.slug}`}
              className="group block rounded-portal-card focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
            >
              <Panel className="flex h-full flex-col p-5 transition-shadow group-hover:shadow-md">
                <Heading variant="card" className="text-portal-ink group-hover:text-portal-accent">
                  {card.title}
                </Heading>
                <p className="mt-2 flex-1 text-portal-small text-portal-muted">{card.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <Pill tone={card.generatedAt ? 'neutral' : 'warning'}>
                    {lastAnalyzed(card.generatedAt)}
                  </Pill>
                  <ArrowRightIcon
                    aria-hidden
                    className="size-4 text-portal-muted transition-transform group-hover:translate-x-0.5 group-hover:text-portal-accent"
                  />
                </div>
              </Panel>
            </Link>
          ))}
        </div>
      ) : (
        <Panel>
          <EmptyState
            title="No targeted analytics yet"
            message="Register one in the API's targeted-analytics definitions and run the daily build."
          />
        </Panel>
      )}
    </div>
  );
}
